import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiGrid, FiPackage, FiUsers, FiShoppingBag, FiTag, FiLogOut, FiMenu, FiX, FiUpload, FiMail, FiSettings, FiDollarSign } from 'react-icons/fi';
import ProductUpload from './ProductUpload';
import Products from './Products';
import Orders from './Orders';
import SubscribersFullView from './SubscribersFullView';
import Settings from './Settings';
import authService from '../../utils/authService';
import Newsletter from './Newsletter';



// Get admin path from environment variables or use the default
const ADMIN_ACCESS_PATH = process.env.REACT_APP_ADMIN_PATH || 'secure-admin-route';

// Update the type definition to include 'subscribers'
type TabType = 'dashboard' | 'products' | 'orders' | 'newsletter' | 'upload' | 'subscribers' | 'settings';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we should activate a specific tab from navigation state
  const initialTab = location.state?.activeTab || 'dashboard';
  
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Validate token on page load
  useEffect(() => {
    // Check if we're authenticated
    const checkAuth = async () => {
      try {
        // Validate session with backend
        const result = await authService.validateSession();
        
        if (!result.success) {
          // If not authenticated, redirect to login
          navigate(`/${ADMIN_ACCESS_PATH}/login`);
        } else if (location.state && location.state.activeTab) {
          // Set active tab if provided in navigation state
          setActiveTab(location.state.activeTab as TabType);
        }
      } catch (error) {
        console.error('Auth validation error:', error);
        // On error, redirect to login
        navigate(`/${ADMIN_ACCESS_PATH}/login`);
      }
    };
    
    checkAuth();
  }, [navigate, location]);

  const handleLogout = async () => {
    try {
      setIsUploading(true);
      const result = await authService.logout();
      
      if (result.success) {
      navigate(`/${ADMIN_ACCESS_PATH}/login`);
      } else {
        console.error('Logout failed:', result.error);
        alert('Logout failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Logout error:', error);
      alert('Logout error. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Real dashboard stats
  const [dashboardStats, setDashboardStats] = useState([
    { id: 1, title: 'Products', value: '0', icon: FiPackage, color: 'bg-blue-500' },
    { id: 2, title: 'Orders', value: '0', icon: FiShoppingBag, color: 'bg-green-500' },
    { id: 3, title: 'Subscribers', value: '0', icon: FiUsers, color: 'bg-purple-500' },
    { id: 4, title: 'Revenue', value: '$0.00', icon: FiTag, color: 'bg-amber-500' },
  ]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  // Fetch real dashboard stats
  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      // Use authService.apiRequest instead of manual fetch to ensure proper auth
      const data = await authService.apiRequest('/api/admin/dashboard-stats', 'GET');
      
      // Update dashboard stats with real data
      setDashboardStats([
        { id: 1, title: 'Products', value: data.stats.products.total.toString(), icon: FiPackage, color: 'bg-blue-500' },
        { id: 2, title: 'Orders', value: data.stats.orders.total.toString(), icon: FiShoppingBag, color: 'bg-green-500' },
        { id: 3, title: 'Subscribers', value: data.stats.subscribers.total.toString(), icon: FiUsers, color: 'bg-purple-500' },
        { id: 4, title: 'Revenue', value: data.stats.revenue.formatted, icon: FiTag, color: 'bg-amber-500' },
      ]);
      
      setRecentOrders(data.recentOrders);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch stats when component mounts or when activeTab changes to dashboard
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboardStats();
    }
  }, [activeTab]);

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'upload':
        return <ProductUpload />;
      case 'products':
        return <Products />;
      case 'subscribers':
        return <SubscribersFullView />;
      case 'orders':
        return <Orders />;
      case 'newsletter':
        return (
          <>
            <h3 className="text-xl font-semibold mb-4">Newsletter</h3>
            <Newsletter />
          </>
        );
      case 'dashboard':
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {dashboardStats.map((stat) => (
                <div 
                  key={stat.id} 
                  className="bg-background p-4 rounded-lg border border-gray-800 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm text-gray-400">{stat.title}</p>
                    <p className="text-2xl font-semibold mt-1">{stat.value}</p>
                  </div>
                  <div className={`h-12 w-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-background p-6 rounded-lg border border-gray-800">
              <h3 className="text-lg font-medium mb-4">Recent Orders</h3>
              <div className="overflow-x-auto -mx-6">
                <div className="inline-block min-w-full align-middle px-6">
                  <div className="overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-800">
                      <thead className="text-left text-sm text-gray-400 border-b border-gray-800">
                        <tr>
                          <th className="px-4 py-3 whitespace-nowrap">Order ID</th>
                          <th className="px-4 py-3 whitespace-nowrap">Product</th>
                          <th className="px-4 py-3 whitespace-nowrap hidden sm:table-cell">Customer</th>
                          <th className="px-4 py-3 whitespace-nowrap hidden md:table-cell">Date</th>
                          <th className="px-4 py-3 whitespace-nowrap">Status</th>
                          <th className="px-4 py-3 whitespace-nowrap text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800">
                        {statsLoading ? (
                          [...Array(5)].map((_, i) => (
                            <tr key={i} className="text-sm">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                                <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                                <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">
                                <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
                              </td>
                            </tr>
                          ))
                        ) : recentOrders.length > 0 ? (
                          recentOrders.map((order: any, i) => (
                            <tr key={order.id} className="text-sm hover:bg-background-light/30 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{order.order_number}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {order.items && order.items.length > 0 ? order.items[0].description : 'N/A'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap hidden sm:table-cell">{order.customer_name || order.customer_email || 'N/A'}</td>
                              <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">{new Date(order.created_at).toLocaleDateString()}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  order.fulfillment_status === 'fulfilled' ? 'bg-green-500/10 text-green-400' : 
                                  order.order_status === 'completed' ? 'bg-green-500/10 text-green-400' : 
                                  order.order_status === 'cancelled' ? 'bg-red-500/10 text-red-400' : 
                                  'bg-amber-500/10 text-amber-400'
                                }`}>
                                  {order.fulfillment_status === 'fulfilled' ? 'Shipped' : 
                                   order.order_status === 'completed' ? 'Completed' : 
                                   order.order_status === 'cancelled' ? 'Cancelled' : 'New'}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">{order.total_amount_formatted}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                              No orders found. Orders will appear here once customers make purchases.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {/* Mobile-friendly data cards, shown only on very small screens */}
              <div className="sm:hidden mt-4 space-y-3">
                <h4 className="text-xs uppercase text-gray-500 mb-2">Recent Orders (Mobile View)</h4>
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-background-light/20 rounded-lg p-3 border border-gray-800">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-400">Order ID</span>
                      <span className="text-xs font-medium">#ORD-{1000 + i}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-400">Product</span>
                      <span className="text-xs font-medium">Air Jordan {i + 1}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-400">Amount</span>
                      <span className="text-xs font-medium">${(150 + i * 25).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-400">Status</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        i % 3 === 0 ? 'bg-green-500/10 text-green-400' : 
                        i % 3 === 1 ? 'bg-amber-500/10 text-amber-400' : 
                        'bg-blue-500/10 text-blue-400'
                      }`}>
                        {i % 3 === 0 ? 'Completed' : i % 3 === 1 ? 'Pending' : 'Processing'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            

          </>
        );
      case 'settings':
        return <Settings />;
      default:
        return <Products />;
    }
  };

  return (
    <div className="min-h-screen bg-background-dark text-primary flex flex-col md:flex-row">
      {/* Mobile header with toggle button */}
      <div className="md:hidden flex items-center justify-between bg-background border-b border-gray-800 h-16 px-4">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 bg-background-light rounded-md text-primary"
          aria-label="Toggle sidebar menu"
        >
          {isSidebarOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
        </button>
        <h1 className="text-lg font-bold">GRAILED804</h1>
        <div className="h-8 w-8 rounded-full bg-accent flex items-center justify-center">
          <FiUsers className="h-4 w-4 text-white" />
        </div>
      </div>

      {/* Sidebar - with improved mobile handling */}
      <aside 
        className={`fixed md:relative inset-y-0 left-0 z-30 w-[280px] md:w-64 bg-background border-r border-gray-800 transition-transform transform ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 md:flex-shrink-0 overflow-y-auto
        ${isSidebarOpen ? 'shadow-2xl md:shadow-none' : ''}`}
      >
        {/* Overlay for mobile to close sidebar when clicking outside */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-20 md:hidden" 
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden="true"
          ></div>
        )}
        
        <div className="h-full flex flex-col relative z-30">
          <div className="flex items-center justify-between h-16 border-b border-gray-800 px-4">
            <h1 className="text-xl font-bold">GRAILED804 Admin</h1>
            <button 
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 md:hidden text-white/60 hover:text-white"
              aria-label="Close sidebar"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>
          
          <nav className="flex-1 px-2 py-4">
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => {
                    setActiveTab('dashboard');
                    setIsSidebarOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-primary rounded-md ${
                    activeTab === 'dashboard' ? 'bg-accent/10' : 'hover:bg-background-light'
                  }`}
                >
                  <FiGrid className={`mr-3 h-5 w-5 ${activeTab === 'dashboard' ? 'text-accent' : ''}`} />
                  Dashboard
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    setActiveTab('products');
                    setIsSidebarOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-primary rounded-md ${
                    activeTab === 'products' ? 'bg-accent/10' : 'hover:bg-background-light'
                  }`}
                >
                  <FiTag className={`mr-3 h-5 w-5 ${activeTab === 'products' ? 'text-accent' : ''}`} />
                  Products
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    setActiveTab('orders');
                    setIsSidebarOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-primary rounded-md ${
                    activeTab === 'orders' ? 'bg-accent/10' : 'hover:bg-background-light'
                  }`}
                >
                  <FiShoppingBag className={`mr-3 h-5 w-5 ${activeTab === 'orders' ? 'text-accent' : ''}`} />
                  Orders
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    setActiveTab('newsletter');
                    setIsSidebarOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-primary rounded-md ${
                    activeTab === 'newsletter' ? 'bg-accent/10' : 'hover:bg-background-light'
                  }`}
                >
                  <FiMail className={`mr-3 h-5 w-5 ${activeTab === 'newsletter' ? 'text-accent' : ''}`} />
                  Newsletter
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    setActiveTab('subscribers');
                    setIsSidebarOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-primary rounded-md ${
                    activeTab === 'subscribers' ? 'bg-accent/10' : 'hover:bg-background-light'
                  }`}
                >
                  <FiMail className={`mr-3 h-5 w-5 ${activeTab === 'subscribers' ? 'text-accent' : ''}`} />
                  Subscribers
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    setActiveTab('upload');
                    setIsSidebarOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-primary rounded-md ${
                    activeTab === 'upload' ? 'bg-accent/10' : 'hover:bg-background-light'
                  }`}
                >
                  <FiUpload className={`mr-3 h-5 w-5 ${activeTab === 'upload' ? 'text-accent' : ''}`} />
                  Upload Products
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    setActiveTab('settings');
                    setIsSidebarOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-primary rounded-md ${
                    activeTab === 'settings' ? 'bg-accent/10' : 'hover:bg-background-light'
                  }`}
                >
                  <FiSettings className={`mr-3 h-5 w-5 ${activeTab === 'settings' ? 'text-accent' : ''}`} />
                  Settings
                </button>
              </li>
            </ul>
          </nav>
          
          <div className="p-4 border-t border-gray-800">
            <button 
              onClick={handleLogout}
              disabled={isUploading}
              className="flex items-center w-full px-4 py-3 text-primary hover:bg-background-light rounded-md"
            >
              <FiLogOut className="mr-3 h-5 w-5" />
              {isUploading ? 'Logging out...' : 'Logout'}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Desktop header */}
        <header className="hidden md:flex bg-background border-b border-gray-800 h-16 items-center justify-between px-4 md:px-6">
          <h2 className="text-lg font-semibold">
            {activeTab === 'subscribers' 
              ? 'Subscribers' 
              : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          </h2>
          <div className="flex items-center">
            <span className="text-sm mr-2">Admin User</span>
            <div className="h-8 w-8 rounded-full bg-accent flex items-center justify-center">
              <FiUsers className="h-4 w-4 text-white" />
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Mobile title */}
          <div className="md:hidden mb-4 mt-1">
            <h2 className="text-lg font-semibold">
              {activeTab === 'subscribers' 
                ? 'Subscribers' 
                : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </h2>
          </div>
          {renderTabContent()}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard; 